<template>
  <Tooltip :text="t('modalTitle')">
    <button type="button" class="btn btn-sm nav-btn-icon" @click="onClickAdd">
      <i class="bi bi-justify-left" />
    </button>
  </Tooltip>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import Tooltip from '@/components/tooltip.vue'
import ModalWindow from '@/components/history/modal.vue'
import { useApi } from 'soykajs/composables/api'
import { useModalStack } from '@/stores/modalstack'

const { push: openModal } = useModalStack()

const { t } = useI18n()

const service = ref('ltms')
const page = ref(1)
const onPage = ref(20)
const dateFrom = ref(null)
const dateTo = ref(null)
const authors = ref([])

/** Досье — серверные фильтры/страницы; остальные сервисы пока грузим целиком. */
const serverMode = computed(() => service.value === 'docparser')

const query = computed(() => {
  const q = {
    service: service.value,
    page: page.value,
    onPage: serverMode.value ? onPage.value : 99999,
  }
  if (serverMode.value) {
    if (dateFrom.value) {
      q.date_from = format(new Date(dateFrom.value), 'yyyy-MM-dd')
    }
    if (dateTo.value) {
      q.date_to = format(new Date(dateTo.value), 'yyyy-MM-dd')
    }
    if (authors.value.length) {
      q.authors = [...authors.value]
    }
  }
  return q
})

const {
  model: histModel,
  refresh,
  pending: loading,
  abort,
} = useApi({
  url: 'audit_trail',
  data: {},
  query,
  // Без watch:[service]: смена сервиса идёт через onSelect → один refresh.
  // Иначе watch + await refresh() давали два запроса и гонку.
})

const modalRef = ref()

async function refreshAudit() {
  // abort внутри refresh тоже есть; явный — сразу снять pending старого LTMS.
  abort()
  await refresh()
}

async function onQueryChange(params) {
  if (params.page != null) page.value = params.page
  if (params.onPage != null) onPage.value = Number(params.onPage)
  if ('dateFrom' in params) dateFrom.value = params.dateFrom
  if ('dateTo' in params) dateTo.value = params.dateTo
  if ('authors' in params) authors.value = params.authors || []
  await refreshAudit()
}

function resetQuery() {
  page.value = 1
  dateFrom.value = null
  dateTo.value = null
  authors.value = []
}

async function onClickAdd() {
  const listData = computed(() => {
    const { history = [], related = {}, authors: serverAuthors = [] } = histModel.value.data || {}
    return { history, related, authors: serverAuthors }
  })
  const serverTotal = computed(() => Number(histModel.value.total_count || 0))
  const serverAuthors = computed(() => listData.value.authors || [])

  openModal({
    component: ModalWindow,
    componentRef: modalRef,
    props: {
      modelName: 'audit_trail',
      modelId: 0,
      headerText: t('modalTitle'),
      options: [
        { value: 'ltms', label: 'LTMS' },
        { value: 'catalogs', label: 'Catalogs' },
        { value: 'docparser', label: t('dossierService') },
      ],
      list: listData,
      loading: loading,
      class: 'history-stack-modal',
      size: 'xl',
      // Селектор модуля в modalstack → onSelect; Excel читает auditService отсюда.
      auditService: service,
      // Серверный режим только для досье; LTMS/Catalogs — клиентские фильтры.
      onQueryChange: computed(() => (service.value === 'docparser' ? onQueryChange : null)),
      serverTotal,
      serverAuthors,
      onSelect: async selected => {
        if (service.value === selected) {
          return
        }
        service.value = selected
        resetQuery()
        // Пока грузится новый сервис — не показывать чужой список.
        histModel.value = { data: { history: [], related: {}, authors: [] }, total_count: 0 }
        await refreshAudit()
        modalRef.value?.listRef?.resetFilters?.({ emitQuery: false })
      },
    },
  })

  service.value = 'ltms'
  resetQuery()
  await refreshAudit()
}
</script>

<i18n>
{
  "en": {
    "modalTitle": "Audit Trail",
    "dossierService": "Dossier"
  },
  "ru": {
    "modalTitle": "Контрольный след",
    "dossierService": "Досье"
  }
}
</i18n>
